import React, { useContext, useEffect } from 'react';
import {
  Route,
  ScrollToTop,
  Routes,
  useNavigate,
} from '@thd-olt-component-react/router';
import { AppContext } from '@thd-nucleus/app-render';
import {
  ExperienceContext,
  useConfigService,
} from '@thd-nucleus/experience-context';
import { ModalProvider } from './contexts/modal-context';
import { Error } from './pages/Error';
import { getRoutes } from './routes';
import { PaymentsProvider } from './contexts/payments-context';
import {
  CustomerRole,
  TCustomerRole,
  TCustomerType,
} from './models/common/customer';
import { DrawerProvider } from './contexts/drawer-context';
import { AddressesProvider } from './contexts/addresses-context';

interface AppProps {
  customerType: TCustomerType;
  customerRole: TCustomerRole;
  serverPages: unknown;
}

interface IAppRoutesProps {
  customerType: TCustomerType;
  customerRole: TCustomerRole;
  serverPages: unknown;
}

const AppRoutes = ({
  customerType,
  customerRole,
  serverPages,
}: IAppRoutesProps) => {
  const { instance } = useContext(AppContext);
  const { channel } = useContext(ExperienceContext);
  const { currentRouteName, currentRouteComponent } = instance.router;
  const routes = getRoutes(
    currentRouteName,
    currentRouteComponent,
    serverPages,
  );
  const isB2B = customerType.toUpperCase() === 'B2B';
  const isMobile = channel === 'mobile';
  const isAdmin = customerRole === CustomerRole.ADMIN;
  const canShowBulkAdd = !isMobile && isB2B && isAdmin;

  const isBulkCardAddMicroUiEnabled = !!useConfigService(
    'BULK_CARD_ADD_MICRO_UI_ENABLED',
  );

  useEffect(() => {
    document.title = 'The Home Depot';
  }, []);

  const Redirect = ({
    route,
    state,
  }: {
    route: string;
    state?: Record<string, boolean>;
  }) => {
    const navigate = useNavigate();
    useEffect(() => {
      navigate(route, {
        replace: true,
        ...(state ? { state } : {}),
      });
    }, [navigate, route, state]);
    return null;
  };

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route
          exact
          path={routes.addresses.path}
          element={
            <routes.addresses.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.instantCheckout.path}
          element={
            isB2B ? (
              <Redirect
                route="/myaccount/payments"
                state={{ hasICAccessErrorState: true }}
              />
            ) : (
              <routes.instantCheckout.component
                customerType={customerType}
                customerRole={customerRole}
              />
            )
          }
        />
        <Route
          exact
          path={routes.payments.path}
          element={
            <routes.payments.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.addCreditCard.path}
          element={
            <routes.addCreditCard.component
              customerType={customerType}
              customerRole={customerRole}
            />
          }
        />
        <Route
          exact
          path={routes.addGiftCard.path}
          element={<routes.addGiftCard.component customerType={customerType} />}
        />
        <Route
          exact
          path={routes.enrollHDWalletFromNativeApp.path}
          element={
            isB2B ? (
              <routes.enrollHDWalletFromNativeApp.component
                customerType={customerType}
                customerRole={customerRole}
              />
            ) : (
              <Redirect route="/myaccount/payments" />
            )
          }
        />
        <Route
          exact
          path={routes.remotePurchasesPerfTest.path}
          element={
            isB2B ? (
              <routes.remotePurchasesPerfTest.component />
            ) : (
              <Redirect route="/myaccount/payments" />
            )
          }
        />
        <Route
          exact
          path={routes.bulkAddCreditCards.path}
          element={
            isB2B && isBulkCardAddMicroUiEnabled && canShowBulkAdd ? (
              <routes.bulkAddCreditCards.component />
            ) : (
              <Redirect
                route="/myaccount/payments"
                state={{ hasBulkAddAccessError: true }}
              />
            )
          }
        />
        <Route path="/error" element={<Error />} />
        <Route element={<Error />} />
      </Routes>
    </>
  );
};

export const App = ({ customerType, customerRole, serverPages }: AppProps) => {
  const isB2B = customerType.toUpperCase() === 'B2B';
  return (
    <AddressesProvider isB2B={isB2B}>
      <PaymentsProvider isB2B={isB2B}>
        <ModalProvider>
          <DrawerProvider>
            <AppRoutes
              customerType={customerType}
              customerRole={customerRole}
              serverPages={serverPages}
            />
          </DrawerProvider>
        </ModalProvider>
      </PaymentsProvider>
    </AddressesProvider>
  );
};
